import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Link,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import customerProfileService from '../../../customerProfile/CustomerProfileService';
import useGetCustomerProfile from '../../../../hooks/useGetCustomerProfile';
import ProfileCombo from '../../../../sharedComponents/ProfileCombo';
import customLinkService from '../CustomLinkService';

const emptyItem = () => ({ url: '', price: '', qty: 1, deliveryFeeJp: '', imageUrl: '' });
const emptyRegister = () => ({ smileName: '', smileNameJp: '', contact: '', channel: 'Facebook', phone: '' });

// Create a custom purchase link FOR a registered customer profile.
// Step 1 (customer): look up an existing profile OR register a new one inline.
// Step 2 (link): enter the item(s) + description, then create the link and copy the URL.
const STATUS_CHIP = {
  pending: { label: 'รอชำระ', color: 'warning' },
  completed: { label: 'ชำระแล้ว', color: 'success' },
  expired: { label: 'หมดอายุ', color: 'default' },
  cancelled: { label: 'ยกเลิกแล้ว', color: 'default' },
};

const CreateCustomLinkModal = ({ isOpen, onClose, shop }) => {
  const [tab, setTab] = useState('create'); // 'create' | 'list'
  const [step, setStep] = useState('customer'); // 'customer' | 'link' | 'done'

  // --- created-links list tab ---
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [listFilterSmile, setListFilterSmile] = useState(''); // filter the list by customer

  // ProfileCombo → filter the list to the picked customer (exact match only).
  const handleListFilter = (smileName) => {
    const match = (profiles || []).find((p) => p.smileName === smileName);
    if (match) setListFilterSmile(match.smileName);
  };
  const visibleLinks = listFilterSmile
    ? links.filter((l) => l.customer?.smileName === listFilterSmile)
    : links;

  const loadLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await customLinkService.listCustomLinks(shop);
      setLinks(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'โหลดรายการลิงก์ไม่สำเร็จ');
    } finally {
      setLoadingLinks(false);
    }
  };

  // Load the list whenever the list tab is shown (or the modal opens on it).
  useEffect(() => {
    if (isOpen && tab === 'list') loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab]);

  const copyLink = (url) => {
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success('คัดลอกลิงก์แล้ว');
    }
  };

  // --- customer step ---
  // Reuse the shared profile search (client-side substring match over the shop's profiles).
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const [showRegister, setShowRegister] = useState(false);
  const [register, setRegister] = useState(emptyRegister());
  const [registering, setRegistering] = useState(false);

  // selected customer carried into the link step
  const [customer, setCustomer] = useState(null);

  // Load the shop's profiles for the ProfileCombo when the modal opens.
  useEffect(() => {
    if (isOpen) getCustomerProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- link step ---
  const [items, setItems] = useState([emptyItem()]);
  const [description, setDescription] = useState('');
  // Sea / air as the customer already told the admin; the checkout opens on it
  // (the customer can still change it).
  const [isAir, setIsAir] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [result, setResult] = useState(null); // { id, token, checkoutUrl }

  const resetAll = () => {
    setTab('create');
    setListFilterSmile('');
    setStep('customer');
    setShowRegister(false);
    setRegister(emptyRegister());
    setCustomer(null);
    setItems([emptyItem()]);
    setDescription('');
    setResult(null);
    setIsAir(false);
  };

  // Cancel a pending link made by mistake. The customer's page then says it was
  // cancelled; links are never edited — cancel and issue a new one.
  const cancelLink = async (id) => {
    if (!window.confirm('ยกเลิกลิงก์นี้? ลูกค้าจะชำระเงินด้วยลิงก์นี้ไม่ได้อีก และจะเห็นข้อความให้ติดต่อแอดมิน')) return false;
    setCancelling(true);
    try {
      await customLinkService.cancelCustomLink(id);
      toast.success('ยกเลิกลิงก์แล้ว');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ยกเลิกลิงก์ไม่สำเร็จ');
      return false;
    } finally {
      setCancelling(false);
    }
  };

  // Right after creating: cancel it and go back to the item form, items kept, so
  // the admin can fix what was wrong and issue a new link.
  const cancelAndFix = async () => {
    if (await cancelLink(result.id)) {
      setResult(null);
      setStep('link');
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  // ProfileCombo calls onChange with the typed/selected smileName. Only act on an EXACT
  // match (a real pick from the dropdown); ignore partial typing.
  const handleProfileSelect = (smileName) => {
    const match = (profiles || []).find((p) => p.smileName === smileName);
    if (match) selectProfile(match);
  };

  const selectProfile = (profile) => {
    setCustomer(profile);
    setStep('link');
  };

  const handleRegister = async () => {
    const r = register;
    if (!r.smileName.trim() || !r.smileNameJp.trim() || !r.contact.trim() || !r.channel) {
      toast.warn('กรอก Smile Name, Smile Name (JP), ช่องทาง และชื่อ Line/Facebook ให้ครบ');
      return;
    }
    setRegistering(true);
    try {
      const res = await customerProfileService.addCustomerProfile({
        smileName: r.smileName.trim(),
        smileNameJp: r.smileNameJp.trim(),
        contact: r.contact.trim(),
        channel: r.channel,
        phone: r.phone.trim(),
        shop,
      });
      toast.success('ลงทะเบียนลูกค้าใหม่สำเร็จ');
      selectProfile(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ลงทะเบียนไม่สำเร็จ');
    } finally {
      setRegistering(false);
    }
  };

  const setItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const handleCreate = async () => {
    const clean = items
      .map((it) => ({
        url: (it.url || '').trim(),
        price: Number(it.price),
        qty: Number(it.qty),
        deliveryFeeJp: it.deliveryFeeJp === '' ? 0 : Number(it.deliveryFeeJp),
        ...(it.imageUrl && it.imageUrl.trim() ? { imageUrl: it.imageUrl.trim() } : {}),
      }))
      .filter((it) => it.url);

    if (clean.length === 0) {
      toast.warn('ใส่รายการสินค้าอย่างน้อย 1 รายการ (ต้องมี URL)');
      return;
    }
    for (const it of clean) {
      if (!(it.price >= 0) || Number.isNaN(it.price)) { toast.warn('ราคาต้องเป็นตัวเลข ≥ 0'); return; }
      if (!(it.qty >= 1)) { toast.warn('จำนวนต้องเป็น ≥ 1'); return; }
      if (it.deliveryFeeJp < 0 || Number.isNaN(it.deliveryFeeJp)) { toast.warn('ค่าส่งในญี่ปุ่นต้องเป็นตัวเลข ≥ 0'); return; }
    }

    setCreating(true);
    try {
      const res = await customLinkService.createCustomLink({
        items: clean,
        description: description.trim() || null,
        customerId: customer.id,
        isAir,
      });
      setResult(res.data);
      setStep('done');
      toast.success('สร้างลิงก์สำเร็จ');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'สร้างลิงก์ไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>สร้างลิงก์สั่งซื้อ (Custom Link)</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="create" label="สร้างลิงก์ใหม่" />
          <Tab value="list" label="ลิงก์ที่สร้างแล้ว" />
        </Tabs>

        {tab === 'create' && (
        <>
        {step === 'customer' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>1. เลือก / ลงทะเบียนลูกค้า</Typography>
            <Box mb={2}>
              <ProfileCombo profiles={profiles || []} onChange={handleProfileSelect} />
            </Box>

            <Box>
              {!showRegister && (
                <Button size="small" onClick={() => { setShowRegister(true); setRegister(emptyRegister()); }}>
                  + ลงทะเบียนลูกค้าใหม่
                </Button>
              )}
                {showRegister && (
                  <Box p={2} sx={{ border: '1px dashed #bbb', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>ลงทะเบียนลูกค้าใหม่</Typography>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                      <TextField size="small" label="Smile Name" value={register.smileName} onChange={(e) => setRegister({ ...register, smileName: e.target.value })} />
                      <TextField size="small" label="Smile Name (JP)" value={register.smileNameJp} onChange={(e) => setRegister({ ...register, smileNameJp: e.target.value })} />
                      <Select size="small" value={register.channel} onChange={(e) => setRegister({ ...register, channel: e.target.value })}>
                        <MenuItem value="Facebook">Facebook</MenuItem>
                        <MenuItem value="Line">Line</MenuItem>
                      </Select>
                      <TextField size="small" label="ชื่อ Line/Facebook" value={register.contact} onChange={(e) => setRegister({ ...register, contact: e.target.value })} />
                      <TextField size="small" label="เบอร์โทร (ถ้ามี)" value={register.phone} onChange={(e) => setRegister({ ...register, phone: e.target.value })} />
                    </Box>
                    <Box mt={1.5}>
                      <Button size="small" variant="contained" onClick={handleRegister} disabled={registering}>
                        {registering ? <CircularProgress size={20} /> : 'ลงทะเบียน & ใช้ลูกค้านี้'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
          </Box>
        )}

        {step === 'link' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              2. รายการสินค้า — ลูกค้า: <b>{customer?.smileName}</b>
              <Button size="small" sx={{ ml: 1 }} onClick={() => setStep('customer')}>เปลี่ยนลูกค้า</Button>
            </Typography>
            {items.map((it, idx) => (
              <Box key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                <TextField size="small" label="URL" sx={{ flex: 3 }} value={it.url} onChange={(e) => setItem(idx, 'url', e.target.value)} />
                <TextField size="small" label="ราคา/ชิ้น ¥" type="number" sx={{ flex: 1 }} value={it.price} onChange={(e) => setItem(idx, 'price', e.target.value)} />
                <TextField size="small" label="จำนวน" type="number" sx={{ width: 80 }} value={it.qty} onChange={(e) => setItem(idx, 'qty', e.target.value)} />
                <TextField size="small" label="ค่าส่ง ¥" type="number" sx={{ flex: 1 }} value={it.deliveryFeeJp} onChange={(e) => setItem(idx, 'deliveryFeeJp', e.target.value)} />
                <TextField size="small" label="รูป (URL)" sx={{ flex: 2 }} value={it.imageUrl} onChange={(e) => setItem(idx, 'imageUrl', e.target.value)} />
                <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addItem}>เพิ่มรายการ</Button>
            <Divider sx={{ my: 2 }} />
            <TextField size="small" fullWidth multiline minRows={2} label="หมายเหตุ (ถ้ามี)" value={description} onChange={(e) => setDescription(e.target.value)} />
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <Typography variant="body2">การจัดส่ง (ที่ลูกค้าต้องการ):</Typography>
              <Select size="small" value={isAir ? 'air' : 'sea'} onChange={(e) => setIsAir(e.target.value === 'air')}>
                <MenuItem value="sea">ทางเรือ</MenuItem>
                <MenuItem value="air">ทางเครื่องบิน</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary">หน้าชำระเงินจะเลือกไว้ให้ ลูกค้าเปลี่ยนเองได้</Typography>
            </Box>
          </Box>
        )}

        {step === 'done' && result && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>สร้างลิงก์สำเร็จ 🎉</Typography>
            <Typography variant="body2" gutterBottom>ลูกค้า: <b>{customer?.smileName}</b> · การจัดส่ง: <b>{isAir ? 'ทางเครื่องบิน' : 'ทางเรือ'}</b></Typography>
            <Box display="flex" gap={1} alignItems="center" mt={1}>
              <Link href={result.checkoutUrl} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>{result.checkoutUrl}</Link>
              <IconButton size="small" onClick={() => copyLink(result.checkoutUrl)}><ContentCopyIcon fontSize="small" /></IconButton>
            </Box>
            <Box mt={2}>
              <Button size="small" color="error" variant="outlined" onClick={cancelAndFix} disabled={cancelling}>
                {cancelling ? <CircularProgress size={18} /> : 'สร้างผิด? ยกเลิกลิงก์นี้แล้วแก้ไข'}
              </Button>
            </Box>
          </Box>
        )}
        </>
        )}

        {tab === 'list' && (
          <Box>
            <Box display="flex" gap={1} alignItems="flex-end" mb={2}>
              <Box flex={1}><ProfileCombo profiles={profiles || []} onChange={handleListFilter} initialSmileName={listFilterSmile} /></Box>
              {listFilterSmile && (
                <Button size="small" onClick={() => setListFilterSmile('')}>ล้างตัวกรอง</Button>
              )}
            </Box>
            {loadingLinks ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
            ) : visibleLinks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {listFilterSmile ? 'ไม่พบลิงก์ของลูกค้ารายนี้' : 'ยังไม่มีลิงก์ที่สร้าง'}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ลูกค้า</TableCell>
                    <TableCell align="right">ยอด (¥)</TableCell>
                    <TableCell align="center">รายการ</TableCell>
                    <TableCell align="center">สถานะ</TableCell>
                    <TableCell>สร้างเมื่อ</TableCell>
                    <TableCell align="center">ลิงก์</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLinks.map((l) => {
                    const chip = STATUS_CHIP[l.status] || { label: l.status, color: 'default' };
                    return (
                      <TableRow key={l.id} hover>
                        <TableCell>
                          <Typography variant="body2"><b>{l.customer?.smileName || '-'}</b></Typography>
                          <Typography variant="caption" color="text.secondary">{l.customer?.contact || ''}</Typography>
                        </TableCell>
                        <TableCell align="right">{Number(l.totalAmount).toLocaleString()}</TableCell>
                        <TableCell align="center">{l.itemCount}</TableCell>
                        <TableCell align="center"><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
                        <TableCell>{l.createdAt ? new Date(l.createdAt).toLocaleString('th-TH') : '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" title="คัดลอกลิงก์" onClick={() => copyLink(l.checkoutUrl)}><ContentCopyIcon fontSize="small" /></IconButton>
                          <IconButton size="small" title="เปิดลิงก์" component="a" href={l.checkoutUrl} target="_blank" rel="noopener"><OpenInNewIcon fontSize="small" /></IconButton>
                          {l.status === 'pending' && (
                            <IconButton size="small" color="error" title="ยกเลิกลิงก์" disabled={cancelling}
                              onClick={async () => { if (await cancelLink(l.id)) loadLinks(); }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {tab === 'create' && step === 'link' && (
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : 'สร้างลิงก์'}
          </Button>
        )}
        {tab === 'create' && step === 'done' && (
          <Button onClick={resetAll}>สร้างลิงก์ใหม่</Button>
        )}
        {tab === 'list' && (
          <Button onClick={loadLinks} disabled={loadingLinks}>รีเฟรช</Button>
        )}
        <Button onClick={handleClose}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomLinkModal;
